import React, { useCallback, useEffect, useState } from "react";
import ProgramQuickCreateModal from "@/components/programs/ProgramQuickCreateModal";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/contexts/AuthContext";

const ADD_NEW_VALUE = "__add_new__";

/**
 * Dropdown for the client form's "Training Program" field.
 * - Lists active (non-archived) programs alphabetically
 * - Auto-includes the currently-assigned value even if it's no longer in the
 *   library (legacy free-text packageType) so historical data is preserved
 * - Shows a "+ Add New Program" sentinel for users with manageUsers/trainer perms
 */
export default function ProgramSelect({
  value, onChange, currentUser,
  onSelectProgram, testId = "training-program-select",
}) {
  const ctx = useAuth();
  const me = currentUser || ctx.user;
  const canCreate = can(me, "manageTrainingPrograms");
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/training-programs");
      setPrograms(res.data);
    } catch {
      // Silent — leave previously-loaded options in place
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function handleChange(e) {
    const next = e.target.value;
    if (next === ADD_NEW_VALUE) {
      if (canCreate) setQuickAddOpen(true);
      return;
    }
    onChange(next);
    if (typeof onSelectProgram === "function") {
      const program = programs.find((p) => p.name === next) || null;
      onSelectProgram(program);
    }
  }

  // If the currently-selected value isn't in the active list (e.g. archived
  // or legacy free-text), show it as a separate disabled option so it doesn't
  // visually disappear.
  const hasCurrentInList = !value || programs.some((p) => p.name === value);

  return (
    <>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={handleChange}
        className="w-full rounded-xl border p-2 font-normal"
        disabled={loading}
      >
        <option value="">{loading ? "Loading programs…" : "Select Program"}</option>
        {!hasCurrentInList && value && (
          <option value={value}>{value} (legacy)</option>
        )}
        {programs.map((p) => (
          <option key={p.id} value={p.name}>
            {p.name}
            {p.defaultPrice > 0 ? ` — $${p.defaultPrice}` : ""}
          </option>
        ))}
        {canCreate && (
          <option value={ADD_NEW_VALUE}>+ Add New Program…</option>
        )}
      </select>
      {quickAddOpen && (
        <ProgramQuickCreateModal
          onCancel={() => setQuickAddOpen(false)}
          onCreated={(p) => {
            setPrograms((prev) => [...prev, p].sort((a, b) => a.name.localeCompare(b.name)));
            onChange(p.name);
            if (typeof onSelectProgram === "function") onSelectProgram(p);
            setQuickAddOpen(false);
          }}
        />
      )}
    </>
  );
}
